import { z } from 'zod';

import { FULLY_MANAGED_ORDER_LIMIT } from '@Common/constants/b2b/limits';
import { ZodAmount, ZodAmountRequest } from '@Common/types/Amount';
import { ZodTaigaId } from '@Common/types/b2b/axa/common';
import {
  ZodB2bPlanTypeEsPeiProfileValues,
  ZodB2bPlanTypeEsPerecoiPiloteValues,
} from '@Common/types/b2b/plans';
import { ZodB2bCashSource } from '@Common/types/bo/b2b/b2b';
import { zodIntegerAmount2 } from '@Common/types/currency/currency';
import {
  ZodMutualFundOrderAgreedTermsArray,
  ZodVoluntaryDeductibility,
} from '@Common/types/explore/mutual_fund_order';
import { ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import { ZodInstrumentMutualFundAssetClass } from '@Common/types/instruments/instrument';
import { ZodInvestmentHorizonAge } from '@Common/types/profile/employee_savings';
import {
  ZodProfileSavingPlanId,
  ZodTransformedProfileSavingPlanId,
} from '@Common/types/profile/saving_plans';
import { ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, type GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodFullyManagedAllocationsBody = z.object({
  saving_plan_id: ZodProfileSavingPlanId,
  template_risk_profile_id: ZodId.nullable().default(null),
  investment_horizon_age: ZodInvestmentHorizonAge.optional(),
});
export type FullyManagedAllocationsBody = z.output<typeof ZodFullyManagedAllocationsBody>;

const ZodFullyManagedAllocationsYear = z.object({
  year: z.number(),
  allocations: z.array(
    z.object({
      allocation_percent: z.number(),
      instrument_id: z.number().int(),
      instrument_name: ZodTrimmedString,
      instrument_risk: z.number().int(),
      instrument_asset_class: ZodInstrumentMutualFundAssetClass,
    }),
  ),
});

const ZodPerecoiPiloteFullyManagedAllocationsResponseBody = z.object({
  plan_type: ZodB2bPlanTypeEsPerecoiPiloteValues,
  data: ZodFullyManagedAllocationsYear.array(),
  instrument_legend: z
    .array(
      z.object({
        id: z.number().int(),
        name: ZodTrimmedString,
        risk_ranking: z
          .number()
          .int()
          .min(0)
          .max(9)
          .describe(
            'Risk ranking of all instruments available in Pilote plan. Range 0-9. 0 being the lowest risk and 9 being the highest risk. Unique for each instrument in the array.',
          ),
      }),
    )
    .max(10), // Frontend is built to accept max 10 instruments as this is how many colors the risk chart accepts. Any changes to this require a corresponding change in the frontend.
});
export type PerecoiPiloteFullyManagedAllocationsResponseBody = z.output<
  typeof ZodPerecoiPiloteFullyManagedAllocationsResponseBody
>;

const ZodPeiProfileFullyManagedAllocationsResponseBody = z.object({
  plan_type: ZodB2bPlanTypeEsPeiProfileValues,
  data: ZodFullyManagedAllocationsYear.array().max(1),
});

export type PeiProfileFullyManagedAllocationsResponseBody = z.output<
  typeof ZodPeiProfileFullyManagedAllocationsResponseBody
>;

export const postExploreEmployeeSavingsFullyManagedAllocationsEndpoint = endpoint({
  method: 'POST',
  body: ZodFullyManagedAllocationsBody,
  responseBody: z.discriminatedUnion('plan_type', [
    ZodPerecoiPiloteFullyManagedAllocationsResponseBody,
    ZodPeiProfileFullyManagedAllocationsResponseBody,
  ]),
});

export type PostExploreEmployeeSavingsFullyManagedAllocationsEndpoint = GetEndpointType<
  typeof postExploreEmployeeSavingsFullyManagedAllocationsEndpoint
>;

export const PostExploreEmployeeSavingsFullyManagedOrderBody = z.object({
  saving_plan_id: ZodTransformedProfileSavingPlanId,
  amount: zodIntegerAmount2.and(z.number().min(10_00).max(FULLY_MANAGED_ORDER_LIMIT)),
  payment_method: ZodB2BPaymentMethod,
  rib_id: z.number().optional(),
  deductibility: ZodVoluntaryDeductibility.optional(),
  agreed_terms: ZodMutualFundOrderAgreedTermsArray,
});

export const postExploreEmployeeSavingsFullyManagedOrderEndpoint = endpoint({
  method: 'POST',
  body: PostExploreEmployeeSavingsFullyManagedOrderBody,
  responseBody: z.object({
    order_correlation_id: z.string().uuid().or(ZodTaigaId),
  }),
});

export type PostExploreEmployeeSavingsFullyManagedOrderEndpoint = GetEndpointType<
  typeof postExploreEmployeeSavingsFullyManagedOrderEndpoint
>;

const ZodMatchingContributionSummaryRequestBody = z.object({
  amount: ZodAmountRequest.optional(),
  saving_plan_ids: ZodProfileSavingPlanId.array().min(1),
  contribution_type: ZodB2bCashSource.optional(),
  allocations: z
    .array(
      z.object({
        saving_plan_id: ZodProfileSavingPlanId,
        instrument_id: z.number().int().nullable(),
        amount: ZodAmountRequest.min(0),
      }),
    )
    .optional(),
});

export type MatchingContributionSummaryRequestBody = z.output<
  typeof ZodMatchingContributionSummaryRequestBody
>;

export const ZodEmployeeSavingsMatchingContribution = z.object({
  gross_amount: ZodAmount,
  remaining_amount: ZodAmount,
  max_amount: ZodAmount,
  invested_amount: ZodAmount,
  resets_date: ZodDateTimeString.nullable(),
  remaining_amount_to_maximize: ZodAmount.nullable(),
  max_rate: z.number(),
  // What can still be received this year, so net of the PASS envelope already consumed elsewhere
  annual_ceiling_amount: ZodAmount.nullable(),
  // The company's own yearly ceiling for the category, before the legal one applies
  plan_ceiling_amount: ZodAmount.nullable(),
  // The legal envelope of the category's plan family, 8% of the PASS (16% for the PERECO family)
  legal_ceiling_amount: ZodAmount.nullable(),
  has_custom_mc_non_estimable: z.boolean().optional(),
});

export type EmployeeSavingsMatchingContribution = z.infer<
  typeof ZodEmployeeSavingsMatchingContribution
>;

export const postExploreEmployeeSavingsMatchingContributionSummaryEndpoint = endpoint({
  method: 'POST',
  body: ZodMatchingContributionSummaryRequestBody,
  responseBody: ZodEmployeeSavingsMatchingContribution.nullable(),
});

export type PostExploreEmployeeSavingsMatchingContributionSummaryEndpoint = GetEndpointType<
  typeof postExploreEmployeeSavingsMatchingContributionSummaryEndpoint
>;

export const postExploreEmployeeSavingsCancelOrderEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    id: ZodTrimmedString,
  }),
  responseBody: ZodSuccessResponse,
});

export type PostExploreEmployeeSavingsCancelOrderEndpoint = GetEndpointType<
  typeof postExploreEmployeeSavingsCancelOrderEndpoint
>;
